import store from "@/store";
import config from "@/config";

export default {
	initialize() {
		if (!this.audioContext && store.getters.isWebRTCStudio()) {
			this.audioContext = new AudioContext();
			this.mixedOutput = this.audioContext.createMediaStreamDestination();
			this.mediaSourcesMap = new Map();
		}

		// eslint-disable-next-line no-undef
		this.nebulaAdapter = new NebulaAdapter({
			root: "/libs/nebula-web-api/protobuf",
			file: "com/radiojar/nebula/NebulaPlayer.proto",
			protocol: "NebulaPlayer"
		});

		const Conf = {
			root: "/libs/nebula-web-api/protobuf",
			file: "com/radiojar/conference/Conference.proto"
		};

		// eslint-disable-next-line no-undef
		this.signaler = new WebRTCSignaler({
			// eslint-disable-next-line no-undef
			adapterProto: NebulaAdapter.prototype,
			protocol: "fluo",
			protoRoot: Conf.root,
			conferenceProto: Conf.file
		});

		this.signaler.onPeerConnectionOpen(this.onPeerConnectionOpen.bind(this));
		this.signaler.onPeerConnectionClosed(this.onPeerConnectionClosed.bind(this));
		this.signaler.onRemoteStreamAdded(this.onRemoteStreamAdded.bind(this));
		this.signaler.onError(this.onError.bind(this));

		this.nebulaAdapter.onOpen(this.onOpen.bind(this));

		this.signaler._dcAdapter.conference.user.onJoined(this.userAdded.bind(this));
		this.signaler._dcAdapter.conference.user.onLeft(this.userLeft.bind(this));
		this.signaler._dcAdapter.conference.user.onPlayFlipped(this.playFlipped.bind(this));
		this.signaler._dcAdapter.conference.user.onGainSet(this.gainSet.bind(this));

		this.nebulaAdapter.nebulaPlayer.console.onMixerGainSet(this.mixerGainSet.bind(this));
		this.nebulaAdapter.nebulaPlayer.console.onChannelGainSet(this.channelGainSet.bind(this));
		this.nebulaAdapter.nebulaPlayer.console.onChannelFlipped(this.channelFlipped.bind(this));
		this.nebulaAdapter.nebulaPlayer.microphone.onAirFlipped(this.airFlipped.bind(this));
		this.nebulaAdapter.nebulaPlayer.microphone.onDuckingAdjusted(this.duckingAdjusted.bind(this));
	},

	//Triggered when the WebRTC peer connection established
	onPeerConnectionOpen() {
		console.log("Peer connection open.");
		store.commit("setSignalerConnectionStatus", this.signaler.isOpen());
		this.nebulaAdapter.connect(this.signaler.pc);
		this.flipPlay(store.getters.isMicOn() ?? false);
	},

	//Triggered when the WebRTC peer connection closed
	onPeerConnectionClosed() {
		console.warn("Peer connection closed.");
		this.closeConnection();
	},

	//Triggered the remote audio stream connected
	onRemoteStreamAdded({stream}) {
		console.log("Remote stream added.");
		store.commit("setWebRTCStream", stream);
		store.commit("setSignalerConnectionStatus", this.signaler.isOpen());
	},

	//Triggered on Signaler error
	onError(e) {
		console.error("Signaler error: [%s]", JSON.stringify(e));
		store.commit("setSignalerConnectionStatus", this.signaler.isOpen());
		this.closeConnection();
	},

	//Triggered on Nebula Adapter open
	onOpen() {
		console.log("Nebula adapter open.");
		store.commit("setNebulaAdapterStatus", this.nebulaAdapter.status());
	},

	//Triggered when a new mic (user) added in the conference
	userAdded({user}) {
		if (user.startsWith("guestp")) {
			this.flipAir(false, user);
			console.log("User added: ", user);
		}
	},

	//Triggered when a mic (user) removed from the conference
	userLeft({user}) {
		console.log("User left: ", user);
	},

	//Triggered when a mic (user) switched on/off
	playFlipped(playFlipped) {
		if (playFlipped.user === this.signaler.user()) {
			store.commit("setMicOnOff", playFlipped.on);
		}
		store.commit("setUserMicState", playFlipped);
	},

	//Triggered when the gain of mic (user) changed
	gainSet({user, value}) {
		console.log(`Gain set: User ${user}, value: ${value}`);
	},

	openConnection(role) {
		const station = store.getters.getWebRTCPlayerKey();
		const component = store.getters.isWebRTCStudio() ? "room" : "console";
		const studio = store.getters.getStudioSettings().reference;
		// Reuse the clientID from the current session if available, otherwise generate a new random one and persist it
		// clientID must be unique per session
		const clientID = sessionStorage.getItem("clientID") ?? Math.random().toString(36).slice(2, 10);
		sessionStorage.setItem("clientID", clientID);
		const user = `${store.getters.getUserID()}_${studio}_${clientID}_${role}`;
		const token = store.getters.getWebRTCToken();

		return new Promise((resolve, reject) => {
			// eslint-disable-next-line no-undef
			let nebulaRouter = new NebulaRouter();
			nebulaRouter
				.playoutRoute(config.ASTRUM_HTTP_HOST, station, component, user, token)
				.then(conferenceUri => {
					console.info("Got user media. Opening signaler.");
					if (store.getters.isWebRTCStudio()) {
						const onOpen = () => {
							this.onPeerConnectionOpen();
							this.signaler.onPeerConnectionOpen(this.onPeerConnectionOpen.bind(this));
							resolve();
						};
						const onError = e => {
							this.onError(e);
							reject(e);
						};
						this.signaler.onPeerConnectionOpen(onOpen);
						this.signaler.onError(onError);
						this.signaler.open(conferenceUri, this.mixedOutput.stream);
					} else {
						const onOpen = () => {
							this.onOpen();
							this.nebulaAdapter.onOpen(this.onOpen.bind(this));
							resolve();
						};
						this.nebulaAdapter.onOpen(onOpen);
						this.nebulaAdapter.connect(conferenceUri);
					}
				})
				.catch(reject);
		});
	},

	connectOutputSource({id, stream, gain = 1}) {
		let output;

		if (this.mediaSourcesMap.has(id)) {
			output = this.mediaSourcesMap.get(id);
		} else {
			output = this.audioContext.createMediaStreamSource(stream);

			// Create a gain node
			const gainNode = this.audioContext.createGain();
			gainNode.gain.value = gain;
			output.connect(gainNode);
		}

		output.connect(this.mixedOutput);
		this.mediaSourcesMap.set(id, output);
	},

	disconnectOutputSource({id}) {
		const output = this.mediaSourcesMap.get(id);
		if (output) {
			output.disconnect(this.mixedOutput);
			this.mediaSourcesMap.delete(id);
		}
	},

	closeConnection(reset = true) {
		this.nebulaAdapter.close();
		this.signaler.close();
		if (reset) {
			this.reset();
		}
	},

	flipPlay(on = false, user = this.signaler.user()) {
		this.signaler._dcAdapter.conference.user.flipPlay(user, on);
	},

	setGain(volume, user = this.signaler.user()) {
		//volume: double (0...1)
		this.signaler._dcAdapter.conference.user.setGain(user, volume);
	},

	reset() {
		store.commit("setWebRTCStream", null);
		store.commit("setSignalerConnectionStatus", false);
		store.commit("setMicOnOff", false);
	},

	mixerGainSet(gain) {
		console.log("Gain set from console master channel", gain);
	},

	channelGainSet(gain) {
		console.log("Gain set from console data channel", gain);
	},

	channelFlipped(flipped) {
		console.log("Channel flipped from console data channel", flipped);
	},

	airFlipped(airFlipped) {
		console.log("Air flipped from console data channel.", airFlipped);
		store.commit("setUserAirState", airFlipped);
	},

	duckingAdjusted(threshold) {
		console.log("Ducking adjusted: ", threshold);
	},

	setMasterGain(gain) {
		if (!this.nebulaAdapter) {
			console.warn("Ignoring setMasterGain: WebRTCService is not initialized yet.");
			return;
		}
		this.nebulaAdapter.nebulaPlayer.console.setMixerGain(gain);
	},

	setChannelGain(gain, channel) {
		if (!this.nebulaAdapter) {
			console.warn("Ignoring setChannelGain: WebRTCService is not initialized yet.");
			return;
		}
		this.nebulaAdapter.nebulaPlayer.console.setChannelGain(gain, channel);
	},

	flipChannel(state, channel) {
		if (!this.nebulaAdapter) {
			console.warn("Ignoring flipChannel: WebRTCService is not initialized yet.");
			return;
		}
		this.nebulaAdapter.nebulaPlayer.console.flipChannel(state, channel.toString());
	},

	flipAir(micOn, user) {
		if (!this.nebulaAdapter) {
			console.warn("Ignoring flipAir: WebRTCService is not initialized yet.");
			return;
		}
		this.nebulaAdapter.nebulaPlayer.microphone.flipAir(micOn, user);
	},

	adjustDucking(threshold) {
		if (!this.nebulaAdapter) {
			console.warn("Ignoring adjustDucking: WebRTCService is not initialized yet.");
			return;
		}
		this.nebulaAdapter.nebulaPlayer.microphone.adjustDucking(threshold);
	}
};
